import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Settings, CreditCard, HelpCircle, LogOut, ChevronDown, BarChart3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const UserAccountDropdown = () => {
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSignOut = async () => {
    setIsOpen(false);
    await signOut();
    navigate('/');
  };

  if (!user) return null;

  const displayName = userProfile?.full_name ||
    `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`?.trim() ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')?.[0] ||
    'User';

  const initials = displayName
    ?.split(' ')
    ?.map(n => n?.[0])
    ?.slice(0, 2)
    ?.join('')
    ?.toUpperCase() || 'U';

  const menuItems = [
    { label: 'Dashboard', href: '/dashboard', icon: BarChart3 },
    { label: 'Profile Settings', href: '/profile-settings', icon: Settings },
    { label: 'Subscription', href: '/subscription-management', icon: CreditCard },
    { label: 'Billing History', href: '/billing-history', icon: CreditCard },
    { label: 'Help & Support', href: '/help-support', icon: HelpCircle },
  ];

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="User account menu"
      >
        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
          {initials}
        </div>
        <div className="hidden sm:block text-left">
          <p className="text-xs font-semibold text-gray-900 leading-tight max-w-[100px] truncate">{displayName}</p>
          <p className="text-xs text-gray-500 leading-tight max-w-[100px] truncate">{user?.email}</p>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform hidden sm:block ${
          isOpen ? 'rotate-180' : ''
        }`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-xl z-50 py-1">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {menuItems?.map((item) => {
              const ItemIcon = item?.icon;
              return (
                <Link
                  key={item?.href}
                  to={item?.href}
                  className="flex items-center space-x-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <ItemIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span>{item?.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Sign Out */}
          <div className="border-t border-gray-100 py-1">
            <button
              onClick={handleSignOut}
              className="flex items-center space-x-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full transition-colors"
            >
              <LogOut className="w-4 h-4 flex-shrink-0" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAccountDropdown;